import {
  ConversationSettingsConfig,
  isConversationSettingsToolId,
  normalizeConversationSettingsTools,
} from '@procureai/agents';

import {
  DemandConversationSettingsQueryQuery,
  SvbrgConversationSettingsAvailableTools,
  SvbrgConversationSettingsQuickActionInput,
  SvbrgConversationSettingsToolsInput,
  SvbrgLanguage,
  SvbrgQuickActionTranslation,
  SvbrgQuickActionTranslationsInput,
  SvbrgQuickActionTranslationVariant,
} from '@/generated/gql/graphql';

const graphqlConversationSettingsToolIds = new Set<string>(Object.values(SvbrgConversationSettingsAvailableTools));

const isGraphQlConversationSettingsToolId = (toolId: string): toolId is SvbrgConversationSettingsAvailableTools => {
  return graphqlConversationSettingsToolIds.has(toolId);
};

const toGraphQlConversationSettingsToolId = (toolId: string): SvbrgConversationSettingsAvailableTools | undefined => {
  if (!isGraphQlConversationSettingsToolId(toolId)) {
    return;
  }

  return toolId;
};

export const transformSettingsToFormData = (
  conversationSettings?: DemandConversationSettingsQueryQuery['sevenbridges']['conversationSettings'],
): ConversationSettingsConfig | undefined => {
  if (!conversationSettings) {
    return;
  }
  const tools: ConversationSettingsConfig['tools'] = normalizeConversationSettingsTools(
    conversationSettings.tools.filter((tool) => isConversationSettingsToolId(tool.id)),
  ).map((tool) => ({
    id: tool.id as ConversationSettingsConfig['tools'][number]['id'],
    isEnabled: tool.isEnabled,
  }));

  const filterTranslationsByVariant = (
    translations: SvbrgQuickActionTranslation[],
    variant: SvbrgQuickActionTranslationVariant,
  ) =>
    translations
      .filter((translation) => translation.variant === variant)
      .map((translation) => ({
        ...translation,
        language: translation.language.toLowerCase(),
      }));

  const quickActions = conversationSettings.quickActions.map((action) => {
    const titleTranslations = filterTranslationsByVariant(
      action.translations,
      SvbrgQuickActionTranslationVariant.TITLE,
    );
    const helpTextTranslations = filterTranslationsByVariant(
      action.translations,
      SvbrgQuickActionTranslationVariant.HELP_TEXT,
    );
    const { isConversationStarter, prompt } = action;

    return {
      prompt,
      titleTranslations,
      helpTextTranslations,
      isConversationStarter,
    };
  });

  return {
    tools,
    quickActions,
  };
};

const transformTranslationsToGraphQl = ({
  titleTranslations,
  helpTextTranslations,
}: {
  titleTranslations: ConversationSettingsConfig['quickActions'][number]['titleTranslations'];
  helpTextTranslations: ConversationSettingsConfig['quickActions'][number]['helpTextTranslations'];
}): SvbrgQuickActionTranslationsInput[] => {
  return [
    ...titleTranslations.map((translation) => ({
      language: translation.language.toUpperCase() as SvbrgLanguage,
      text: translation.text,
      variant: SvbrgQuickActionTranslationVariant.TITLE,
    })),
    ...helpTextTranslations.map((translation) => ({
      language: translation.language.toUpperCase() as SvbrgLanguage,
      text: translation.text,
      variant: SvbrgQuickActionTranslationVariant.HELP_TEXT,
    })),
  ];
};

export const transformCreateQuickActionsToGraphQl = (
  quickActions: ConversationSettingsConfig['quickActions'],
): SvbrgConversationSettingsQuickActionInput[] => {
  return quickActions.map(({ prompt, titleTranslations, helpTextTranslations, isConversationStarter }) => ({
    prompt: prompt,
    translations: transformTranslationsToGraphQl({ titleTranslations, helpTextTranslations }),
    isConversationStarter: isConversationStarter,
  }));
};

export const transformToolsToGraphQl = (
  tools: ConversationSettingsConfig['tools'],
): SvbrgConversationSettingsToolsInput[] => {
  return tools
    .map((tool) => ({
      id: toGraphQlConversationSettingsToolId(tool.id),
      isEnabled: tool.isEnabled,
    }))
    .filter((tool): tool is SvbrgConversationSettingsToolsInput => Boolean(tool.id));
};
